/**
 * Push notification subscription.
 *
 * Standard Web Push, not Firebase: the browser already has a push service, so
 * there is no SDK to ship to a field phone and no extra vendor account. The
 * server handles VAPID signing and payload encryption (services/webPush.ts).
 *
 * WHEN THE PERMISSION IS ASKED FOR
 *
 * Not on first load. A prompt that appears before someone knows what the app
 * is gets denied, and a denied permission cannot be re-requested — the user
 * has to dig through browser settings to undo it. So it is asked for after
 * sign-in, when the person has a reason to want it, and only once.
 */

import { api } from './api';
import * as idb from './idb';

const ASKED_KEY = 'pushPermissionAsked';

/** Written by `subscribe` when it discards a subscription bound to an old key. */
const DIAG_KEY_RESUBSCRIBED = 'pushResubscribedAt';

/**
 * Written by the SERVICE WORKER on every push it receives — see sw.ts.
 *
 * This is the single most useful fact when a notification does not appear,
 * because it splits the problem in half:
 *
 *   timestamp present and recent  → the push ARRIVED and the notification did
 *                                   not display. An Android notification
 *                                   channel, Do Not Disturb, or battery
 *                                   optimisation.
 *   timestamp absent or old       → the push never arrived. Subscription,
 *                                   VAPID key, or the push service.
 *
 * Without it, both look identical from the outside: a green "sent" toast and
 * a silent phone, which is exactly where this problem has been stuck.
 */
const DIAG_KEY_LAST_PUSH = 'lastPushReceivedAt';

/**
 * Decode a base64url VAPID key into bytes for `pushManager.subscribe`.
 *
 * Built from an explicit `ArrayBuffer` and with the return type left to
 * inference on purpose. TypeScript 5.7 made `Uint8Array` generic over its
 * backing buffer, so a bare `Uint8Array` annotation widens to
 * `Uint8Array<ArrayBufferLike>` — which includes `SharedArrayBuffer` and is
 * therefore rejected where a `BufferSource` is required. Allocating the buffer
 * here keeps the narrow type, and works on both older and newer compilers.
 */
function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function keyToBase64Url(key: ArrayBuffer | null): string {
  if (!key) return '';
  const bytes = new Uint8Array(key);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export type PushState = 'unsupported' | 'denied' | 'granted' | 'default';

export function pushState(): PushState {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushState;
}

/**
 * Subscribe this device, sending the result to the server.
 *
 * Safe to call repeatedly: an existing subscription is reused rather than
 * replaced, and re-posting it is how a device that was previously retired
 * (its browser storage cleared, say) gets marked working again.
 */
export async function subscribe(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };

  const registration = await navigator.serviceWorker.ready;

  const { configured, publicKey } = await api.get<{ configured: boolean; publicKey?: string }>(
    '/api/settings/push-key',
  );
  if (!configured || !publicKey) return { ok: false, reason: 'server_not_configured' };

  let subscription = await registration.pushManager.getSubscription();

  /**
   * A SUBSCRIPTION IS BOUND TO THE VAPID KEY IT WAS CREATED WITH.
   *
   * This function used to reuse any existing subscription without checking
   * which key it carries. That is fine until the server's VAPID keypair
   * changes — and it did: the public key originally stored was not a valid
   * point on the P-256 curve and had to be re-derived from the private key.
   *
   * Every device that subscribed before that fix holds a subscription bound to
   * the OLD public key. The server now signs its JWT with the new private key,
   * the push service compares the two, and rejects the request with 403. No
   * notification arrives, the app has no idea, and re-running subscribe()
   * changes nothing because the stale subscription is reused for ever.
   *
   * That fits the reported symptom exactly: desktop Chrome works (subscribed
   * after the fix, or its data was cleared) and the phone does not (subscribed
   * before it). It is also invisible from the phone — the permission is
   * granted, a subscription exists, and everything looks correct.
   *
   * So compare, and re-subscribe when they differ. `options.applicationServerKey`
   * is the browser's own record of what the subscription was made with, which
   * is the only trustworthy source here.
   */
  if (subscription) {
    const boundKey = keyToBase64Url(subscription.options.applicationServerKey ?? null);
    if (boundKey && boundKey !== publicKey) {
      console.warn('PUSH_KEY_STALE', {
        note: 'This subscription was created with a different VAPID key, so the push service '
            + 'will reject every message with 403. Re-subscribing.',
        boundKeyPrefix: boundKey.slice(0, 12),
        serverKeyPrefix: publicKey.slice(0, 12),
      });
      await subscription.unsubscribe().catch(() => {});
      subscription = null;
      await idb.kvSet(DIAG_KEY_RESUBSCRIBED, new Date().toISOString());
    }
  }

  if (!subscription) {
    if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
    subscription = await registration.pushManager.subscribe({
      // Required by Chrome: a subscription that could deliver silently is
      // refused outright.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await api.post('/api/settings/push-subscription', {
    endpoint: subscription.endpoint,
    p256dh: keyToBase64Url(subscription.getKey('p256dh')),
    auth: keyToBase64Url(subscription.getKey('auth')),
  });

  return { ok: true };
}

export interface PushDiagnostics {
  supported: boolean;
  permission: string;
  /** Is a service worker controlling this page right now? */
  serviceWorkerActive: boolean;
  subscribed: boolean;
  /** Which push service the subscription points at — FCM, WNS, Mozilla. */
  pushService: string | null;
  /** Does the subscription's VAPID key match the server's current one? */
  keyMatchesServer: boolean | null;
  /** When the service worker last actually received a push. */
  lastPushReceivedAt: string | null;
  /** When a stale-key subscription was last discarded and replaced. */
  resubscribedAt: string | null;
  /** True when this looks like an installed PWA rather than a browser tab. */
  installed: boolean;
}

/**
 * Everything knowable about push on THIS device, for the settings screen.
 *
 * WHY THIS IS WORTH A SCREEN
 *
 * Android push has failed for weeks with no information beyond "the
 * notification did not come". Every layer reports success: the server gets a
 * 201 from the push service, the app shows a green toast, the permission is
 * granted, a subscription exists. Somewhere in that chain the message stops,
 * and nothing anywhere says where.
 *
 * These seven facts are enough to locate it, and they can be read off a phone
 * that is not plugged into a laptop — which matters, because the device that
 * fails is a field phone in somebody's pocket, not the machine with DevTools.
 */
export async function diagnostics(): Promise<PushDiagnostics> {
  const base: PushDiagnostics = {
    supported: pushSupported(),
    permission: pushSupported() ? Notification.permission : 'unsupported',
    serviceWorkerActive: !!navigator.serviceWorker?.controller,
    subscribed: false,
    pushService: null,
    keyMatchesServer: null,
    lastPushReceivedAt: await idb.kvGet<string>(DIAG_KEY_LAST_PUSH) ?? null,
    resubscribedAt: await idb.kvGet<string>(DIAG_KEY_RESUBSCRIBED) ?? null,
    // `display-mode: standalone` is how an installed PWA presents itself.
    // Worth knowing: notification behaviour differs between an installed app
    // and a browser tab on Android.
    installed: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
  };

  if (!base.supported) return base;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return base;

    base.subscribed = true;

    // The host tells you which push service is involved, and therefore whose
    // rules apply. FCM and WNS fail in different ways.
    try {
      base.pushService = new URL(subscription.endpoint).host;
    } catch {
      base.pushService = 'unparseable endpoint';
    }

    const { publicKey } = await api.get<{ configured: boolean; publicKey?: string }>(
      '/api/settings/push-key',
    );
    const boundKey = keyToBase64Url(subscription.options.applicationServerKey ?? null);
    if (publicKey && boundKey) base.keyMatchesServer = boundKey === publicKey;
  } catch {
    // A diagnostics call must never throw. Partial information is the point.
  }

  return base;
}

export async function unsubscribe(): Promise<void> {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
  await api.del('/api/settings/push-subscription').catch(() => {
    // The local subscription is already gone; a failed server call would be
    // corrected on the next subscribe.
  });
}

/**
 * Ask once, after sign-in.
 *
 * Deliberately silent about failure. A driver who declines notifications still
 * has a working app, and nagging them on every login would be worse than the
 * missed notification.
 */
export async function requestOnceAfterLogin(): Promise<void> {
  if (!pushSupported()) return;
  if (Notification.permission !== 'default') {
    // Already granted or already refused. If granted, re-post the subscription
    // so a device retired by the server comes back.
    if (Notification.permission === 'granted') await subscribe().catch(() => {});
    return;
  }

  const asked = await idb.kvGet<boolean>(ASKED_KEY);
  if (asked) return;
  await idb.kvSet(ASKED_KEY, true);

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') await subscribe();
  } catch {
    // Older browsers throw on the promise form. Not worth handling further.
  }
}
